/**
 * Fields settings — workspace-wide custom metadata fields (v1, migration 0022).
 *
 * Owner-only (sidebar item is access:"owner"; server gates on ACTIONS.MANAGE_FIELDS,
 * reported via canManage). Three types: select (colored options), text, date.
 * Values are set per-asset in the watch panel; select values also render as chips
 * on cards and can filter the project grid. Guests never see fields.
 */

import { useEffect, useState } from "react";
import { Check, GripVertical, Loader2, Pencil, Plus, Trash2, ArrowUp, ArrowDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { SettingsPageHeader } from "@/pages/settings/settings-placeholder";
import {
  useFieldDefs,
  useCreateFieldDef,
  useUpdateFieldDef,
  useDeleteFieldDef,
  useReorderFieldDefs,
  type FieldDef,
  type FieldOption,
  type FieldType,
} from "@/hooks/use-fields";
import { cn } from "@/lib/utils";

const TYPE_LABELS: Record<FieldType, string> = {
  select: "Single-select",
  text: "Text",
  date: "Date",
};
const DEFAULT_SWATCH = "#8b5cf6";

export function FieldsSettingsPage() {
  const { data, isLoading, isError } = useFieldDefs();
  const reorder = useReorderFieldDefs();
  const del = useDeleteFieldDef();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<FieldDef | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<FieldDef | null>(null);

  const fields = data?.fields ?? [];
  const canManage = data?.canManage ?? false;

  function openCreate() {
    setEditing(null);
    setDialogOpen(true);
  }
  function openEdit(def: FieldDef) {
    setEditing(def);
    setDialogOpen(true);
  }

  function move(index: number, dir: -1 | 1) {
    const next = [...fields];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    reorder.mutate(next.map((f) => f.id));
  }

  if (isLoading) {
    return (
      <div>
        <SettingsPageHeader title="Fields" subtitle="Custom metadata you can set on any asset." />
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-[62px] animate-pulse rounded-xl border border-border bg-secondary/30" />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div>
        <SettingsPageHeader title="Fields" subtitle="Custom metadata you can set on any asset." />
        <div className="rounded-xl border border-border bg-secondary/30 px-4 py-3 text-sm text-muted-foreground">
          Couldn't load your fields. Refresh to try again.
        </div>
      </div>
    );
  }

  return (
    <div>
      <SettingsPageHeader title="Fields" subtitle="Custom metadata you can set on any asset." />

      {!canManage && (
        <div className="mb-6 rounded-xl border border-border bg-secondary/30 px-4 py-3 text-sm text-muted-foreground">
          Only the workspace owner can manage fields.
        </div>
      )}

      {fields.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border py-16 text-center">
          <p className="text-sm text-muted-foreground">
            No fields yet. Add one to start tagging assets with a status, note, or date.
          </p>
          {canManage && (
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4" /> New field
            </Button>
          )}
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-2">
            {fields.map((f, i) => (
              <div
                key={f.id}
                className="flex items-center gap-3 rounded-xl border border-border bg-secondary/30 px-4 py-3"
              >
                {canManage && (
                  <div className="flex shrink-0 flex-col text-muted-foreground">
                    <button
                      type="button"
                      aria-label="Move up"
                      disabled={i === 0 || reorder.isPending}
                      onClick={() => move(i, -1)}
                      className="grid h-4 w-5 place-items-center rounded hover:text-foreground disabled:opacity-30"
                    >
                      <ArrowUp className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      aria-label="Move down"
                      disabled={i === fields.length - 1 || reorder.isPending}
                      onClick={() => move(i, 1)}
                      className="grid h-4 w-5 place-items-center rounded hover:text-foreground disabled:opacity-30"
                    >
                      <ArrowDown className="h-3 w-3" />
                    </button>
                  </div>
                )}
                <GripVertical className="hidden h-4 w-4 shrink-0 text-muted-foreground/40" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium text-foreground">{f.label}</span>
                    <span className="shrink-0 rounded-md border border-border bg-background px-1.5 py-0.5 text-[11px] text-muted-foreground">
                      {TYPE_LABELS[f.type]}
                    </span>
                  </div>
                  {f.type === "select" && f.options.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {f.options.map((o) => (
                        <FieldOptionChip key={o.value} option={o} />
                      ))}
                    </div>
                  )}
                </div>
                {canManage && (
                  <div className="flex shrink-0 items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(f)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setConfirmDelete(f)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
          {canManage && (
            <Button variant="outline" className="mt-4" onClick={openCreate}>
              <Plus className="h-4 w-4" /> New field
            </Button>
          )}
        </>
      )}

      <FieldDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
      />

      <DeleteFieldDialog
        field={confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        onConfirm={async () => {
          if (!confirmDelete) return;
          await del.mutateAsync(confirmDelete.id);
          setConfirmDelete(null);
        }}
        pending={del.isPending}
      />
    </div>
  );
}

function FieldOptionChip({ option }: { option: FieldOption }) {
  const color = option.color;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs"
      style={
        color
          ? { borderColor: color, color, backgroundColor: `${color}1a` }
          : undefined
      }
    >
      {color && <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />}
      {option.value}
    </span>
  );
}

// ── Create / edit dialog ───────────────────────────────────────────────────────

function FieldDialog({
  open,
  onOpenChange,
  editing,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: FieldDef | null;
}) {
  const create = useCreateFieldDef();
  const update = useUpdateFieldDef();
  const isEdit = !!editing;

  const [label, setLabel] = useState("");
  const [type, setType] = useState<FieldType>("select");
  const [options, setOptions] = useState<FieldOption[]>([{ value: "", color: DEFAULT_SWATCH }]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (editing) {
      setLabel(editing.label);
      setType(editing.type);
      setOptions(editing.type === "select" && editing.options.length ? editing.options : [{ value: "", color: DEFAULT_SWATCH }]);
    } else {
      setLabel("");
      setType("select");
      setOptions([{ value: "", color: DEFAULT_SWATCH }]);
    }
  }, [open, editing]);

  const pending = create.isPending || update.isPending;

  function setOption(i: number, patch: Partial<FieldOption>) {
    setOptions((cur) => cur.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));
  }
  function addOption() {
    setOptions((cur) => [...cur, { value: "", color: DEFAULT_SWATCH }]);
  }
  function removeOption(i: number) {
    setOptions((cur) => (cur.length <= 1 ? cur : cur.filter((_, idx) => idx !== i)));
  }

  async function submit() {
    setError(null);
    const trimmedLabel = label.trim();
    if (!trimmedLabel) {
      setError("Give the field a name.");
      return;
    }
    let cleanOptions: FieldOption[] = [];
    if (type === "select") {
      cleanOptions = options
        .map((o) => ({ value: o.value.trim(), color: o.color }))
        .filter((o) => o.value);
      if (cleanOptions.length === 0) {
        setError("A single-select field needs at least one option.");
        return;
      }
      const lower = cleanOptions.map((o) => o.value.toLowerCase());
      if (new Set(lower).size !== lower.length) {
        setError("Options must be unique.");
        return;
      }
    }
    try {
      if (isEdit && editing) {
        await update.mutateAsync({
          id: editing.id,
          label: trimmedLabel,
          ...(editing.type === "select" ? { options: cleanOptions } : {}),
        });
      } else {
        await create.mutateAsync({
          label: trimmedLabel,
          type,
          ...(type === "select" ? { options: cleanOptions } : {}),
        });
      }
      onOpenChange(false);
    } catch (err) {
      setError((err as Error)?.message || "Something went wrong.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit field" : "New field"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Rename the field or adjust its options. The field type can't be changed."
              : "Fields apply across your whole workspace and can be set on any asset."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-1">
          <div>
            <label htmlFor="field-label" className="mb-1.5 block text-sm font-medium text-foreground">
              Name
            </label>
            <Input
              id="field-label"
              value={label}
              maxLength={60}
              placeholder="e.g. Review status"
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>

          <div>
            <span className="mb-1.5 block text-sm font-medium text-foreground">Type</span>
            <div className="flex gap-1.5">
              {(["select", "text", "date"] as FieldType[]).map((t) => (
                <button
                  key={t}
                  type="button"
                  disabled={isEdit}
                  onClick={() => setType(t)}
                  className={cn(
                    "flex-1 rounded-lg border px-3 py-2 text-sm transition-colors",
                    type === t
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border text-muted-foreground hover:text-foreground",
                    isEdit && "cursor-not-allowed opacity-60",
                  )}
                >
                  {TYPE_LABELS[t]}
                </button>
              ))}
            </div>
            {isEdit && (
              <p className="mt-1.5 text-xs text-muted-foreground">
                Type is locked after creation to keep existing values valid.
              </p>
            )}
          </div>

          {type === "select" && (
            <div>
              <span className="mb-1.5 block text-sm font-medium text-foreground">Options</span>
              <div className="flex flex-col gap-2">
                {options.map((o, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="color"
                      aria-label="Option color"
                      value={o.color || DEFAULT_SWATCH}
                      onChange={(e) => setOption(i, { color: e.target.value.toLowerCase() })}
                      className="h-9 w-10 shrink-0 cursor-pointer rounded-md border border-border bg-background"
                    />
                    <Input
                      value={o.value}
                      maxLength={60}
                      placeholder={`Option ${i + 1}`}
                      onChange={(e) => setOption(i, { value: e.target.value })}
                    />
                    <button
                      type="button"
                      aria-label="Remove option"
                      disabled={options.length <= 1}
                      onClick={() => removeOption(i)}
                      className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
              <Button variant="ghost" size="sm" className="mt-2" onClick={addOption}>
                <Plus className="h-3.5 w-3.5" /> Add option
              </Button>
            </div>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={pending}>
            {pending ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…
              </>
            ) : isEdit ? (
              "Save changes"
            ) : (
              "Create field"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteFieldDialog({
  field,
  onOpenChange,
  onConfirm,
  pending,
}: {
  field: FieldDef | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  pending: boolean;
}) {
  return (
    <Dialog open={!!field} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete "{field?.label}"?</DialogTitle>
          <DialogDescription>
            This removes the field from every asset. Values already set will stop showing. This can't be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={onConfirm}
            disabled={pending}
          >
            {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Delete field
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
